import React, { useEffect, useRef, useState } from "react";
import SwachhataSurveyForm from "./SwachhataSurveyForm";
import useBackdropClose from "../useBackdropClose";
import SwachhataSurveyScene, { BURST_COLOURS } from "./SwachhataSurveyScene";
import { SURVEY_TITLE, SURVEY_TAGLINE } from "./swachhataSurvey";
import shsLogo from "../../../assets/images/swachhata/shs-logo-2026.png";
import "../EkPed3Modal.css";
import "./SwachhataSurvey.css";
import "./SwachhataSurveyStage.css";

/*
 * Swachhata Hi Seva 2026 — school Solid Waste Management report popup. Same
 * workflow as the Ek Ped Maa Ke Naam 3.0 popup (EkPed3Modal).
 *
 * Auto-shown (by OutletRouteSchool) while the school has NOT filled the form:
 *   "ask"       "Has your school participated in Swachhata Hi Seva 2026?"
 *                 Yes → form     No → "encourage" → Dashboard
 *
 * Opened from the dashboard ("Fill Form" / "Update Details", `direct`):
 *   straight into the form, prefilled when the school has already submitted.
 *
 * After saving: "done" → Download certificate / Dashboard.
 *
 * The decision screens are a split card: copy on the left, the animated bin
 * yard (SwachhataSurveyScene) on the right, reacting to the Yes/No hover.
 */

const BURST = Array.from({ length: 44 }, (_, k) => {
  const angle = (k * 137.5 * Math.PI) / 180;
  const dist = 90 + ((k * 53) % 170);
  return {
    "--tx": `${Math.round(Math.cos(angle) * dist)}px`,
    "--ty": `${Math.round(Math.sin(angle) * dist - 60)}px`,
    "--r": `${(k * 97) % 720}deg`,
    "--cl": BURST_COLOURS[k % BURST_COLOURS.length],
    "--w": `${6 + (k % 3) * 3}px`,
  };
});

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Arrow = () => (
  <svg className="shsx-choice__arrow" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10h12M11 5l5 5-5 5" />
  </svg>
);

const Card = ({ mood, hint, burst, children }) => (
  <div className="shsx-card">
    <div className="shsx-tricolour" aria-hidden="true" />
    <div className="shsx-copy">{children}</div>
    <div className="shsx-stage">
      <SwachhataSurveyScene mood={mood} hint={hint} />
    </div>
    {burst && (
      <div className="shsx-burst" style={{ left: burst.x, top: burst.y }} aria-hidden="true">
        {BURST.map((s, k) => (
          <span key={k} style={s} />
        ))}
      </div>
    )}
  </div>
);

const Brand = ({ small }) => (
  <>
    <div className="shsx-brand">
      <img src={shsLogo} alt="Swachhata Hi Seva 2026" className="shsx-logo" />
      <span className="shsx-kicker">
        <i aria-hidden="true" />
        {"Solid Waste Management Drive · Swachh Paathshala: Yuva for Swachhata"}
      </span>
    </div>
    <h2 className={`shsx-title${small ? " shsx-title--sm" : ""}`}>
      <span>Swachhata Hi Seva</span>
      <span className="shsx-year">2026</span>
    </h2>
    <p className="shsx-tagline">{SURVEY_TAGLINE}</p>
  </>
);

const STEPS = [
  {
    title: "Install the four bins",
    text: "Green for wet, blue for dry, red for sanitary and black for special care waste.",
    bg: "#e6f5e9",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true">
        {["#22a447", "#1e6fd9", "#d93a3a", "#2b2b2b"].map((c, k) => (
          <g key={c} transform={`translate(${1 + k * 8.2} 0)`}>
            <rect x="-0.2" y="4" width="7.4" height="3.4" rx="1" fill={c} />
            <path d="M0.4 8.6h6.2l-0.9 21h-4.4Z" fill={c} />
          </g>
        ))}
      </svg>
    ),
  },
  {
    title: "Hold an awareness activity",
    text: "A rally, an assembly talk or a competition with your students.",
    bg: "#fff1e0",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true" fill="none" stroke="#e07a12" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 14v6h5l11 6V8L10 14Z" fill="#ffd8a8" />
        <path d="M26 12q3 5 0 10M29 9q5 8 0 16" />
      </svg>
    ),
  },
  {
    title: "Compost wet waste",
    text: "Where you can, turn kitchen and garden waste into manure.",
    bg: "#f1ecdf",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true">
        <path d="M4 22h26l-3 9H7Z" fill="#8a5a3b" />
        <path d="M6 22q11-5 22 0" fill="#6b4226" />
        <path d="M17 20v-8" stroke="#2f9a4a" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M17 13C17 8 13 5 8 5 8 10 12 13 17 13ZM17 11c0-4 3-7 8-7 0 5-3 7-8 7Z" fill="#3fb45a" />
      </svg>
    ),
  },
];

export default function SwachhataSurveyModal({ isOpen, direct = false, participation, onClose, onSaved, onDone, onCertificate }) {
  const survey = participation?.survey || null;
  const exists = survey?.status === "submitted";
  const [step, setStep] = useState("ask"); // 'ask' | 'encourage' | 'form' | 'done'
  const [created, setCreated] = useState(false);
  const [mood, setMood] = useState("idle");
  const [burst, setBurst] = useState(null);
  const timer = useRef(null);

  // Only on open: the status refetch after saving flips `exists`, and that must
  // not throw the school out of the thank-you step.
  useEffect(() => {
    if (isOpen) setStep(direct || exists ? "form" : "ask");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setMood("idle");
    setBurst(null);
  }, [step]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Clicking the dimmed area around the card closes the popup: on the thank-you
  // step that is the same as "Dashboard", everywhere else the same as closing.
  const backdrop = useBackdropClose(step === "done" ? onDone : onClose);

  if (!isOpen) return null;

  const chooseYes = (e) => {
    if (burst) return;
    if (reducedMotion()) return setStep("form");
    const card = e.currentTarget.closest(".shsx-card").getBoundingClientRect();
    const btn = e.currentTarget.getBoundingClientRect();
    setMood("party");
    setBurst({ x: btn.left + btn.width / 2 - card.left, y: btn.top + btn.height / 2 - card.top });
    timer.current = setTimeout(() => setStep("form"), 750);
  };

  const hover = (m) => ({
    onMouseEnter: () => !burst && setMood(m),
    onFocus: () => !burst && setMood(m),
    onMouseLeave: () => !burst && setMood("idle"),
    onBlur: () => !burst && setMood("idle"),
  });

  return (
    <div className="ekped3-overlay" role="dialog" aria-modal="true" aria-label={SURVEY_TITLE} {...backdrop}>
      {step === "ask" && (
        <Card mood={mood} hint burst={burst}>
          <Brand />
          <p className="shsx-q">Has your school participated in Swachhata Hi Seva 2026 – Solid Waste Management activities?</p>
          <div className="shsx-choices">
            <button type="button" className="shsx-choice shsx-choice--yes" onClick={chooseYes} {...hover("yes")}>
              <span className="shsx-choice__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5 10 17l9-10" />
                </svg>
              </span>
              <span className="shsx-choice__text">
                <b>Yes</b>
                <small>Fill in our school&apos;s report</small>
              </span>
              <Arrow />
            </button>
            <button type="button" className="shsx-choice shsx-choice--no" onClick={() => setStep("encourage")} {...hover("no")}>
              <span className="shsx-choice__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="#e07a12" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20v-7" />
                  <path d="M12 13C12 8 8.5 5 4 5c0 4.5 3.5 8 8 8ZM12 11c0-4 2.8-7 7-7 0 4.2-2.8 7-7 7Z" fill="#ffd8a8" />
                </svg>
              </span>
              <span className="shsx-choice__text">
                <b>No</b>
                <small>Show us how to take part</small>
              </span>
              <Arrow />
            </button>
          </div>
        </Card>
      )}

      {step === "encourage" && (
        <Card mood="idle" hint>
          <Brand small />
          <p className="shsx-lead">Take part in Swachhata Hi Seva 2026 at your school, then come back here to fill in your school&apos;s report.</p>
          <ol className="shsx-steps">
            {STEPS.map((s, k) => (
              <li key={s.title} className="shsx-step">
                <span className="shsx-step__icon" style={{ "--bg": s.bg }}>
                  {s.icon}
                  <em>{k + 1}</em>
                </span>
                <span>
                  <b>{s.title}</b>
                  <span>{s.text}</span>
                </span>
              </li>
            ))}
          </ol>
          <div className="shsx-choices shsx-choices--one">
            <button type="button" className="shsx-choice shsx-choice--yes" onClick={onClose}>
              <span className="shsx-choice__text">
                <b>Got it, take me to Dashboard</b>
              </span>
              <Arrow />
            </button>
          </div>
          <button type="button" className="shsx-back" onClick={() => setStep("ask")}>← Back</button>
        </Card>
      )}

      {step === "form" && (
        <div className="ekped3-form-modal shsf-form-modal">
          <SwachhataSurveyForm
            answers={exists ? survey.answers : null}
            backLabel={direct ? "Close" : "Back to Selection"}
            onBack={() => (direct ? onClose() : setStep("ask"))}
            onSuccess={(res) => {
              setCreated(!!res?.created);
              setStep("done");
              onSaved && onSaved(res);
            }}
          />
        </div>
      )}

      {step === "done" && (
        <Card mood="party" hint={false}>
          <div className="shsx-medal" aria-hidden="true">
            <svg viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="30" fill="#1f9d4d" />
              <circle cx="32" cy="32" r="24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
              <path className="shsx-tick" d="M19 33.5 28 42l17-19" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="shsx-title">
            <span>Thank you!</span>
          </h2>
          <p className="shsx-lead">
            {created
              ? "Your school's Swachhata Hi Seva 2026 report has been submitted. Your participation certificate is ready to download."
              : "Your school's Swachhata Hi Seva 2026 report has been updated."}
          </p>
          <div className="shsx-choices">
            <button type="button" className="shsx-choice shsx-choice--yes" onClick={onCertificate}>
              <span className="shsx-choice__text">
                <b>Certificate</b>
                <small>Download it now</small>
              </span>
              <Arrow />
            </button>
            <button type="button" className="shsx-choice shsx-choice--no" onClick={onDone}>
              <span className="shsx-choice__text">
                <b>Dashboard</b>
                <small>Go back to your dashboard</small>
              </span>
              <Arrow />
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
